import { useEffect, useRef, useState } from 'react'

const useImageURLValidator = (url: string, fallbackUrl = '', useV2 = false) => {
  const isMountedRef = useRef(true)

  const [imageState, setImageState] = useState<{
    validatedUrl: string
    isLoading: boolean
  }>(() => {
    if (!useV2) {
      return { validatedUrl: '', isLoading: true }
    }

    if (!url) {
      return { validatedUrl: fallbackUrl, isLoading: false }
    }

    const img = new Image()
    img.src = url

    if (img.complete && img.naturalHeight !== 0) {
      return { validatedUrl: url, isLoading: false }
    }

    return { validatedUrl: '', isLoading: true }
  })

  useEffect(() => {
    isMountedRef.current = true

    if (useV2) {
      if (!url) {
        setImageState({ validatedUrl: fallbackUrl, isLoading: false })
        return () => {}
      }

      const img = new Image()
      img.src = url

      if (img.complete && img.naturalHeight !== 0) {
        if (isMountedRef.current) {
          setImageState({ validatedUrl: url, isLoading: false })
        }
      } else {
        img.onload = () => {
          if (isMountedRef.current) {
            setImageState({ validatedUrl: url, isLoading: false })
          }
        }

        img.onerror = () => {
          if (isMountedRef.current) {
            setImageState({ validatedUrl: fallbackUrl, isLoading: false })
          }
        }
      }

      return () => {
        isMountedRef.current = false
        img.onload = null
        img.onerror = null
      }
    }

    const validateImageURL = (
      imageUrl: string,
      fallback: string
    ): Promise<string> =>
      new Promise((resolve) => {
        const img = new Image()
        img.src = imageUrl || fallback

        img.onload = () => resolve(img.src)
        img.onerror = () => resolve(fallback)
      })

    setImageState({ validatedUrl: '', isLoading: true })
    validateImageURL(url, fallbackUrl)
      .then((validated) => {
        if (isMountedRef.current) {
          setImageState({ validatedUrl: validated, isLoading: false })
        }
      })
      .catch(() => {
        if (isMountedRef.current) {
          setImageState({ validatedUrl: fallbackUrl, isLoading: false })
        }
      })

    return () => {
      isMountedRef.current = false
    }
  }, [url, fallbackUrl, useV2])

  return imageState
}

export default useImageURLValidator
